/**
 * @fileoverview Business event mutations (submit, attendance, pre-register).
 * @module logic/businessEvents/businessEventsMutations
 */

import {
  HTTP_CONFLICT,
  HTTP_UNAUTHORIZED,
  HTTP_UNPROCESSABLE_ENTITY,
  fetchUserRoleData,
  getAuthJsonHeaders,
  logDevError,
  resolveAuthExpiredPayload,
  resolveExpiredToken,
  uploadEventBannerFromDataUrl,
} from "../../utils/eventApi.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
import {
  getEventDetails,
} from "./businessEventsQueries.js";
import {
  getParticipatingCompanies,
  getParticipatingCompaniesAdmin,
} from "./businessEventsParticipation.js";

const FAILED_TO_CREATE_EVENT = "Failed to create event:";
const ERROR_SUBMITTING_EVENT = "Error submitting event:";

const submitBusinessEventRequest = (eventData, eventId) => {
  const url = eventId
    ? `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}`
    : `${process.env.REACT_APP_ROOT_API}businessEvent`;
  const method = eventId ? "PUT" : "POST";
  return fetch(url, {
    method,
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(eventData),
  });
};

const completeSuccessfulBusinessEventSubmit = async ({
  data,
  eventData,
  wasCreate,
  setEventData,
  setHasChanges,
}) => {
  if (!data.event) {
    return { ok: false };
  }
  if (data.presignedUrl) {
    const { Buffer } = await import("buffer");
    await uploadEventBannerFromDataUrl(
      data.presignedUrl,
      eventData.imagenBanner,
      eventData.formatoImagen,
      Buffer,
    );
  }
  setEventData(data.event);
  setHasChanges(false);
  return { ok: true, wasCreate, event: data.event };
};

/**
 * Cancels a user's attendance for a business event.
 * @async
 * @param {Object} selectedEvent - Event object with _id property
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} API response data
 * @throws {Error} If cancellation fails
 */
export const cancelAttendance = async (selectedEvent, navigate) => {
  const userId = localStorage.getItem("userId");
  const options = {
    method: "PUT",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      idUsuario: userId,
      idEvento: selectedEvent._id,
      estatus: true,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/attendance/cancel`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await cancelAttendance(selectedEvent, navigate);
    });
    if (tokenResult?.redirected) {
      return null;
    }
    if (tokenResult) {
      return null;
    }

    return data;
  } catch (error) {
    logDevError("Error cancelling attendance:", error);
    throw error;
  }
};

/**
 * Updates local state after attendance cancellation and calls the cancel API.
 * @async
 * @param {Array} eventsData - Current events array
 * @param {Object} selectedEvent - Event being cancelled
 * @param {Function} setEventsData - State setter for events
 * @param {Function} setSelectedEvent - State setter for selected event
 * @param {Function} setCancelAttendanceModalOpen - State setter for modal visibility
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<void>}
 */
export const updateAttendanceCancelledStatus = async (
  eventsData,
  selectedEvent,
  setEventsData,
  setSelectedEvent,
  setCancelAttendanceModalOpen,
  navigate,
) => {
  const updatedEventsData = eventsData.map((event) => {
    if (event._id === selectedEvent._id) {
      return {
        ...event,
        isUserRegistered: false,
      };
    }
    return event;
  });

  try {
    await cancelAttendance(selectedEvent, navigate);
    setEventsData(updatedEventsData);
  } catch (error) {
    logDevError("Error updating cancelled status:", error);
  } finally {
    setSelectedEvent(null);
    setCancelAttendanceModalOpen(false);
  }
};

/**
 * Creates or updates a business event with optional image upload.
 * @async
 * @param {Object} eventData - Event form data including imagenBanner
 * @param {string|null} eventId - Event ID for updates, null for new events
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setSaving - State setter for saving indicator
 * @param {Function} setEventData - State setter for event data
 * @param {Function} setHasChanges - State setter for unsaved changes flag
 * @returns {Promise<{ ok: boolean, wasCreate?: boolean, event?: Record<string, unknown> }|undefined>}
 *   `{ ok: true, wasCreate, event }` on success, `{ ok: false }` on failure, or `undefined` if redirected after token expiry.
 */
export const submitEvent = async (
  eventData,
  eventId,
  navigate,
  setSaving,
  setEventData,
  setHasChanges,
) => {
  setSaving(true);
  const wasCreate = !eventId;
  try {
    const response = await submitBusinessEventRequest(eventData, eventId);
    if (response.ok) {
      const data = await response.json();
      return completeSuccessfulBusinessEventSubmit({
        data,
        eventData,
        wasCreate,
        setEventData,
        setHasChanges,
      });
    }

    const errorData = await response.json();
    const tokenResult = await resolveExpiredToken(errorData, navigate, () =>
      submitEvent(
        eventData,
        eventId,
        navigate,
        setSaving,
        setEventData,
        setHasChanges,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { ok: false };
      }
      return tokenResult.result;
    }
    logDevError(FAILED_TO_CREATE_EVENT, response.status, errorData.message);
    return { ok: false };
  } catch (error) {
    logDevError(ERROR_SUBMITTING_EVENT, error);
    return { ok: false };
  } finally {
    setSaving(false);
  }
};

/**
 * Initializes the event creation/edit page with user roles and event data.
 * @async
 * @param {object} options
 * @param {string|null} options.eventId - Event ID for editing, null for new events
 * @param {string} options.userId - User identifier
 * @param {Function} options.changePermissions - Callback to update permissions
 * @param {Function} options.navigate - React Router navigation function
 * @param {Object} options.dayjs - Day.js library instance
 * @param {Function} options.setFormData - State setter for form data
 * @param {Function} options.setParticipatingCompanies - State setter for companies
 * @param {Function} options.setIsLoading - State setter for loading indicator
 * @param {boolean} options.isFromAdmin - Whether user is accessing from admin panel
 * @param {boolean} [options.skipParticipatingCompanies] - Skip loading participating companies
 * @returns {Promise<void>}
 */
export const initializeEventCreation = async ({
  eventId,
  userId,
  changePermissions,
  navigate,
  dayjs,
  setFormData,
  setParticipatingCompanies,
  setIsLoading,
  isFromAdmin,
  skipParticipatingCompanies = false,
}) => {
  try {
    setIsLoading(true);

    // Get user role data first
    await fetchUserRoleData(userId, changePermissions, navigate);

    // If editing an existing event, get its details
    if (eventId) {
      await getEventDetails(eventId, navigate, dayjs, setFormData);
      if (!skipParticipatingCompanies) {
        if (isFromAdmin) {
          await getParticipatingCompaniesAdmin(
            eventId,
            navigate,
            setParticipatingCompanies,
          );
        } else {
          await getParticipatingCompanies(
            eventId,
            navigate,
            setParticipatingCompanies,
          );
        }
      }
    }
  } catch (error) {
    logDevError("Error initializing event creation:", error);
  } finally {
    setIsLoading(false);
  }
};

/**
 * Validates pre-register form data and loads user/company profile fields.
 * @async
 * @param {string} eventId - Business event id (path param)
 * @param {string} userId - Authenticated user id (path param)
 * @param {{ genero: string, rangoEdad: string, cargo: string, pais: string, estado: string, ciudad: string, telefonoCodigoPais: string|null, telefono: string }} formPayload - Form body (Spanish labels)
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<{ success: true, data: Object }|{ success: false, status?: number, message?: string, fieldsMissing?: string[], fieldsFound?: Object }|undefined>}
 */
export const validatePreRegister = async (
  eventId,
  userId,
  formPayload,
  navigate,
) => {
  const {
    genero,
    rangoEdad,
    cargo,
    pais,
    estado,
    ciudad,
    telefonoCodigoPais,
    telefono,
  } = formPayload;

  const requestBody = {
    genero,
    rangoEdad,
    cargo: cargo.trim(),
    pais,
    estado,
    ciudad,
    telefonoCodigoPais,
    telefono: telefono?.trim?.() ?? telefono ?? "",
  };

  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(requestBody),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/${eventId}/pre-register/validate/${userId}`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveAuthExpiredPayload(data, navigate, () =>
      validatePreRegister(eventId, userId, formPayload, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return {
          success: false,
          status: HTTP_UNAUTHORIZED,
          message: "Unauthorized",
        };
      }
      return tokenResult.result;
    }

    if (response.ok && data?.status === "OK") {
      return { success: true, data: data.data };
    }

    if (response.status === HTTP_CONFLICT) {
      return {
        success: false,
        status: HTTP_CONFLICT,
        alreadyRegistered: true,
        message: data?.message,
      };
    }

    if (response.status === HTTP_UNPROCESSABLE_ENTITY) {
      return {
        success: false,
        status: HTTP_UNPROCESSABLE_ENTITY,
        message: data?.message,
        fieldsMissing: Array.isArray(data?.fieldsMissing)
          ? data.fieldsMissing
          : [],
        fieldsFound: data?.fieldsFound,
      };
    }

    return {
      success: false,
      status: response.status,
      message: data?.message,
      fieldsMissing: data?.fieldsMissing,
    };
  } catch (error) {
    logDevError("Error validating pre-register:", error);
    return {
      success: false,
      message: apiMessageOrGeneric(undefined),
    };
  }
};
